/**
 * 语音播放服务工具
 * 提供统一的语音播放功能，可在多个组件中复用
 */

// 语音播放服务工厂
const createSpeechService = (type = 'system') => {
  // 系统自带的语音合成
  if (type === 'system') {
    // 系统语音不需要缓存，因为它是本地合成的
    return {
      speak: (text, options = {}, silentPreload = false) => {
        if (!window.speechSynthesis) {
          console.error('当前浏览器不支持语音合成API');
          return { cancel: () => {} };
        }
        const utterance = new SpeechSynthesisUtterance(text);
        
        // 设置语音参数
        utterance.lang = options.lang || 'en-US';
        utterance.rate = options.rate || 1;
        utterance.pitch = options.pitch || 1;
        utterance.volume = silentPreload ? 0 : (options.volume || 1); // 如果是预加载模式，音量设为0
        
        // 如果有指定声音，则设置
        if (options.voice) {
          const voices = window.speechSynthesis.getVoices();
          const selectedVoice = voices.find(voice => voice.name === options.voice);
          if (selectedVoice) {
            utterance.voice = selectedVoice;
          }
        }
        
        // 如果是静默预加载模式，则不播放或静音播放
        if (silentPreload) {
          // 系统语音无法真正预加载，但可以通过静音播放来模拟
          // 某些浏览器可能会忽略静音播放，这取决于浏览器的实现
          window.speechSynthesis.speak(utterance);
          return {
            cancel: () => {
              window.speechSynthesis.cancel();
            }
          };
        }
        
        // 播放语音
        window.speechSynthesis.speak(utterance);
        
        return {
          cancel: () => {
            window.speechSynthesis.cancel();
          }
        };
      },
      getVoices: () => {
        return window.speechSynthesis 
          ? window.speechSynthesis.getVoices()
              .filter(voice => voice.lang.includes('en-US'))
              .map(voice => ({
                id: voice.voiceURI,
                name: voice.name
              }))
          : [];
      },
      isSupported: () => {
        return !!window.speechSynthesis;
      }
    };
  }
  
  // API服务语音合成（预留）
  if (type === 'api') {
    return {
      speak: (text, options = {}) => {
        console.log('使用API服务播放:', text, options);
        // 这里将来可以实现API调用的语音服务
        return {
          cancel: () => {
            console.log('取消API语音播放');
          }
        };
      },
      getVoices: () => {
        return []; // 将来可以从API获取可用的声音列表
      },
      isSupported: () => {
        return true; // 假设API服务总是可用的
      }
    };
  }
  
  // 本地音频文件（预留）
  if (type === 'local') {
    return {
      speak: (text, options = {}) => {
        console.log('使用本地音频播放:', text, options);
        // 这里将来可以实现本地音频文件的播放
        return {
          cancel: () => {
            console.log('取消本地音频播放');
          }
        };
      },
      getVoices: () => {
        return []; // 将来可以返回本地可用的音频文件列表
      },
      isSupported: () => {
        return true; // 假设本地音频总是可用的
      }
    };
  }

  // 有道词典API语音
  if (type === 'youdao') {
    // 添加音频缓存
    const audioCache = {};
    
    return {
      speak: (text, options = {}, silentPreload = false) => {
        const audioType = options.audioType || 1; // 1: 英式发音, 2: 美式发音
        const cacheKey = `${text}_${audioType}`;
        
        // 检查缓存中是否已有该音频
        if (audioCache[cacheKey]) {
          const audio = audioCache[cacheKey];
          // 重置音频到起始位置
          audio.currentTime = 0;
          
          // 如果是静默预加载模式，则不播放
          if (silentPreload) {
            return {
              cancel: () => {}
            };
          }
          
          // 播放音频
          audio.play().catch(error => {
            console.error('有道词典语音播放失败:', error);
          });
          
          return {
            cancel: () => {
              audio.pause();
              audio.currentTime = 0;
            }
          };
        }
        
        // 缓存中没有，创建新的音频
        const audioBaseUrl = 'http://dict.youdao.com/dictvoice?audio=';
        const audioUrl = `${audioBaseUrl}${encodeURIComponent(text)}&type=${audioType}`;
        
        const audio = new Audio(audioUrl);
        
        // 将音频添加到缓存
        audioCache[cacheKey] = audio;
        
        // 如果是静默预加载模式，则不播放
        if (silentPreload) {
          // 只加载不播放
          audio.load();
          return {
            cancel: () => {}
          };
        }
        
        // 播放音频
        audio.play().catch(error => {
          console.error('有道词典语音播放失败:', error);
        });
        
        return {
          cancel: () => {
            audio.pause();
            audio.currentTime = 0;
          }
        };
      },
      getVoices: () => {
        return [
          { name: '英式发音', id: 1 },
          { name: '美式发音', id: 2 }
        ];
      },
      isSupported: () => {
        return typeof Audio !== 'undefined';
      },
      // 添加清理缓存的方法
      clearCache: () => {
        Object.values(audioCache).forEach(audio => {
          audio.pause();
          audio.src = '';
        });
        Object.keys(audioCache).forEach(key => delete audioCache[key]);
      }
    };
  }
  
  // 百度语音API
  if (type === 'baidu') {
    // 添加音频缓存
    const audioCache = {};
    
    return {
      speak: (text, options = {}, silentPreload = false) => {
        const lan = options.lan || 'uk'; // uk: 英式发音, en: 美式发音
        const spd = options.spd || 3; // 语速，默认3
        const cacheKey = `${text}_${lan}_${spd}`;
        
        // 检查缓存中是否已有该音频
        if (audioCache[cacheKey]) {
          const audio = audioCache[cacheKey];
          // 重置音频到起始位置
          audio.currentTime = 0;
          
          // 如果是静默预加载模式，则不播放
          if (silentPreload) {
            return {
              cancel: () => {}
            };
          }
          
          // 播放音频
          audio.play().catch(error => {
            console.error('百度语音播放失败:', error);
          });
          
          return {
            cancel: () => {
              audio.pause();
              audio.currentTime = 0;
            }
          };
        }
        
        // 缓存中没有，创建新的音频
        const audioBaseUrl = 'https://sensearch.baidu.com/gettts?source=alading&text=';
        const audioUrl = `${audioBaseUrl}${encodeURIComponent(text)}&lan=${lan}&spd=${spd}`;
        
        const audio = new Audio(audioUrl);
        
        // 将音频添加到缓存
        audioCache[cacheKey] = audio;
        
        // 如果是静默预加载模式，则不播放
        if (silentPreload) {
          // 只加载不播放
          audio.load();
          return {
            cancel: () => {}
          };
        }
        
        // 播放音频
        audio.play().catch(error => {
          console.error('百度语音播放失败:', error);
        });
        
        return {
          cancel: () => {
            audio.pause();
            audio.currentTime = 0;
          }
        };
      },
      getVoices: () => {
        return [
          { name: '英式发音', id: 'uk' },
          { name: '美式发音', id: 'en' }
        ];
      },
      isSupported: () => {
        return typeof Audio !== 'undefined';
      },
      // 添加清理缓存的方法
      clearCache: () => {
        Object.values(audioCache).forEach(audio => {
          audio.pause();
          audio.src = '';
        });
        Object.keys(audioCache).forEach(key => delete audioCache[key]);
      }
    };
  }
  
  // Asugar API语音服务
  if (type === 'asugar') {
    // 添加音频缓存
    const audioCache = {};
    // API授权令牌
    const API_TOKEN = 'KAdQIaOdSfoEXqkNXqvbw';
    // API端点
    const API_ENDPOINT = 'https://api.asugar.app/v1/audio/speech';
    
    return {
      speak: (text, options = {}, silentPreload = false) => {
        const model = options.model || 'tts-1'; // 默认模型
        const voice = options.voice || 'en-US-AriaNeural'; // 默认声音
        const cacheKey = `${text}_${model}_${voice}`;
        
        // 检查缓存中是否已有该音频
        if (audioCache[cacheKey]) {
          const audio = audioCache[cacheKey];
          // 重置音频到起始位置
          audio.currentTime = 0;
          
          // 如果是静默预加载模式，则不播放
          if (silentPreload) {
            return {
              cancel: () => {}
            };
          }
          
          // 播放音频
          audio.play().catch(error => {
            console.error('Asugar语音播放失败:', error);
          });
          
          return {
            cancel: () => {
              audio.pause();
              audio.currentTime = 0;
            }
          };
        }
        
        // 缓存中没有，调用API获取音频
        const audio = new Audio();
        
        // 设置加载中状态
        const loadingPromise = new Promise((resolve, reject) => {
          // 准备API请求
          fetch(API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${API_TOKEN}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              model: model,
              input: text,
              voice: voice
            })
          })
          .then(response => {
            if (!response.ok) {
              throw new Error(`API请求失败: ${response.status}`);
            }
            return response.blob();
          })
          .then(blob => {
            // 创建音频URL
            const audioUrl = URL.createObjectURL(blob);
            audio.src = audioUrl;
            
            // 将音频添加到缓存
            audioCache[cacheKey] = audio;
            
            // 如果是静默预加载模式，则不播放
            if (silentPreload) {
              return Promise.resolve();
            }
            
            // 播放音频
            return audio.play();
          })
          .then(() => {
            resolve();
          })
          .catch(error => {
            console.error('Asugar语音API调用失败:', error);
            reject(error);
          });
        });
        
        return {
          cancel: () => {
            audio.pause();
            audio.currentTime = 0;
            // 如果有URL对象，释放它
            if (audio.src && audio.src.startsWith('blob:')) {
              URL.revokeObjectURL(audio.src);
            }
          },
          // 返回加载Promise，以便调用者可以等待加载完成
          loadingPromise
        };
      },
      getVoices: () => {
        return [
          { name: 'Aria (英语-美国)', id: 'en-US-AriaNeural' },
          { name: 'Guy (英语-美国)', id: 'en-US-GuyNeural' },
          { name: 'Jenny (英语-美国)', id: 'en-US-JennyNeural' },
          { name: 'Sara (英语-英国)', id: 'en-GB-SaraNeural' },
          { name: 'Ryan (英语-英国)', id: 'en-GB-RyanNeural' }
        ];
      },
      isSupported: () => {
        return typeof Audio !== 'undefined' && typeof fetch !== 'undefined';
      },
      // 添加清理缓存的方法
      clearCache: () => {
        Object.values(audioCache).forEach(audio => {
          audio.pause();
          // 如果有URL对象，释放它
          if (audio.src && audio.src.startsWith('blob:')) {
            URL.revokeObjectURL(audio.src);
          }
          audio.src = '';
        });
        Object.keys(audioCache).forEach(key => delete audioCache[key]);
      }
    };
  }
  
  // 默认返回一个空实现
  return {
    speak: () => ({ cancel: () => {} }),
    getVoices: () => [],
    isSupported: () => false
  };
};

// 初始化语音合成系统
const initSpeechSynthesis = () => {
  if (!window.speechSynthesis) {
    console.error('当前浏览器不支持语音合成API');
    return false;
  }
  
  try {
    // 尝试获取语音列表，这会初始化语音合成系统
    window.speechSynthesis.getVoices();
    
    // 播放一个空白的语音，以初始化语音系统
    const utterance = new SpeechSynthesisUtterance('');
    utterance.volume = 0; // 设置音量为0，用户听不到
    window.speechSynthesis.speak(utterance);
    
    console.log('语音合成系统已初始化');
    return true;
  } catch (error) {
    console.error('初始化语音合成失败:', error);
    return false;
  }
};

// 播放单词的通用函数
const playWord = (word, options = {}) => {
  if (!word) return { cancel: () => {} };
  
  const speechService = createSpeechService(options.type || 'system');
  
  if (!speechService.isSupported()) {
    console.error('当前环境不支持语音播放');
    return { cancel: () => {} };
  }

  // 为有道词典API添加特殊处理
  if (options.type === 'youdao') {
    return speechService.speak(word, {
      audioType: options.audioType || 1 // 默认使用英式发音
    });
  }
  
  // 为百度API添加特殊处理
  if (options.type === 'baidu') {
    return speechService.speak(word, {
      lan: options.lan || 'uk', // 默认使用英式发音
      spd: options.spd || 3 // 默认语速
    });
  }
  
  // 为Asugar API添加特殊处理
  if (options.type === 'asugar') {
    return speechService.speak(word, {
      model: options.model || 'tts-1', // 默认模型
      voice: options.voice || 'en-US-AriaNeural' // 默认声音
    }, options.silentPreload);
  }
  
  return speechService.speak(word, {
    lang: 'en-US',
    rate: options.rate || 0.8,
    pitch: options.pitch || 1,
    volume: options.volume || 1,
    voice: options.voice
  });
};

// 获取所有可用的语音类型
const getAvailableVoiceTypes = () => {
  const types = [];
  
  if (window.speechSynthesis) {
    types.push({
      id: 'system',
      name: '系统语音'
    });
  }
  
  if (typeof Audio !== 'undefined') {
    types.push({
      id: 'youdao',
      name: '有道词典'
    });
    
    types.push({
      id: 'baidu',
      name: '百度语音'
    });
    
    // 添加Asugar API语音服务
    if (typeof fetch !== 'undefined') {
      types.push({
        id: 'asugar',
        name: '测试语音'
      });
    }
  }
  
  return types;
};
export {
  createSpeechService,
  initSpeechSynthesis,
  playWord,
  getAvailableVoiceTypes
};